import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Alert, Paper, Divider, Link, Chip, Tooltip } from '@mui/material';
import { getParticipantById, fetchAgeGroups, AgeGroup, ParticipantRank, fetchContestMetadata, ContestField } from '../api/contests';
import { rankChipColor, rankDisplayLabel, rankTitle, rankPriority, normalizeRankType } from '../utils/ranks';
import { formatSecondsToHHMMSS } from '../utils/timeResult';

interface ParticipantProfileProps {
  participantId: string;
}

type ParticipantData = {
  age?: number;
  age_group_id?: number;
  age_group_name?: string;
  weight?: number;
  info?: string;
  links?: { link: string; description?: string }[];
  ranks?: ParticipantRank[];
};

type RankGroup = { type: string; rows: ParticipantRank[] };

const groupRanksByType = (ranks: ParticipantRank[] = []): RankGroup[] => {
  // A participant can satisfy multiple thresholds in the same contest+group.
  // For display we want the BEST rank per (contest_id, result_group), i.e. the newer/higher rank replaces the lower.
  const bestByContext = new Map<string, ParticipantRank>();
  for (const r of ranks) {
    const t = normalizeRankType(r.rank_type);
    if (!t) continue;
    const key = `${String(r.contest_id ?? '')}::${String(r.result_group ?? '')}`;
    const prev = bestByContext.get(key);
    if (!prev || rankPriority(t) > rankPriority(normalizeRankType(prev.rank_type))) {
      bestByContext.set(key, r);
    }
  }

  const map = new Map<string, ParticipantRank[]>();
  for (const r of bestByContext.values()) {
    const t = normalizeRankType(r.rank_type);
    if (!t) continue;
    const arr = map.get(t) ?? [];
    arr.push(r);
    map.set(t, arr);
  }

  const groups: RankGroup[] = Array.from(map.entries()).map(([type, rows]) => ({ type, rows }));
  groups.sort((a, b) => {
    const pa = rankPriority(a.type);
    const pb = rankPriority(b.type);
    if (pa !== pb) return pb - pa;
    if (a.rows.length !== b.rows.length) return b.rows.length - a.rows.length;
    return a.type.localeCompare(b.type);
  });
  return groups;
};

const ParticipantProfilePage: React.FC<ParticipantProfileProps> = ({ participantId }) => {
  const [participantData, setParticipantData] = useState<ParticipantData | null>(null);
  const [loading, setLoading] = useState(true);
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);

  // contest_id -> isTimeMainResult
  const [contestIsTimeMap, setContestIsTimeMap] = useState<Map<number, boolean>>(new Map());

  // Fetch age groups on component mount
  useEffect(() => {
    fetchAgeGroups()
      .then(response => setAgeGroups(response))
      .catch(error => console.error('Error fetching age groups:', error));
  }, []);

  // Fetch participant data
  useEffect(() => {
    const loadParticipantData = async () => {
      try {
        const data = await getParticipantById(participantId);
        if (data) {
          const ageGroupName = ageGroups.find(group => group.age_group_id === data.age_group_id)?.age_group_name;
          setParticipantData({ ...data, age_group_name: ageGroupName });
        }
      } catch (error) {
        console.error('Error loading participant profile:', error);
      } finally {
        setLoading(false);
      }
    };

    // Only load participant data after age groups are fetched
    if (ageGroups.length > 0) {
      loadParticipantData();
    }
  }, [participantId, ageGroups]);

  // After participant data is loaded, fetch contest metadata for contests referenced in ranks
  useEffect(() => {
    const ranks = participantData?.ranks ?? [];
    if (!ranks.length) return;

    const ids = Array.from(new Set(ranks.map((r) => Number(r.contest_id)).filter((x) => Number.isFinite(x))));
    if (!ids.length) return;

    let cancelled = false;

    (async () => {
      const map = new Map<number, boolean>();
      // preserve existing values to avoid flicker
      for (const [k, v] of contestIsTimeMap.entries()) map.set(k, v);

      await Promise.all(
        ids.map(async (id) => {
          if (map.has(id)) return;
          try {
            const fields: ContestField[] = await fetchContestMetadata(String(id));
            const mainField: any = fields.find((f) => (f as any).main_result_field) || fields.find((f) => (f as any).result_field);
            const isTime = String(mainField?.data_type ?? '').toLowerCase() === 'time';
            map.set(id, isTime);
          } catch {
            map.set(id, false);
          }
        })
      );

      if (!cancelled) setContestIsTimeMap(map);
    })();

    return () => {
      cancelled = true;
    };
  }, [participantData, contestIsTimeMap]);

  const formatRankThreshold = (r: ParticipantRank): string | null => {
    if (r.result === null || r.result === undefined) return null;
    const isTime = contestIsTimeMap.get(Number(r.contest_id)) === true;
    if (isTime) {
      return formatSecondsToHHMMSS(Number(r.result));
    }
    return String(r.result);
  };

  if (loading) return <CircularProgress />;

  if (!participantData) {
    return <Alert severity="warning">Профиль участника не найден.</Alert>;
  }

  return (
    <Paper elevation={3} sx={{ p: 4, borderRadius: '10px', marginTop: 2 }}>
      <Typography variant="h5" gutterBottom color="primary">
        Профиль участника
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Box mb={3}>
        <Typography variant="body1"><strong>Возраст:</strong> {participantData.age ?? 'Неизвестно'}</Typography>
        <Typography variant="body1"><strong>Возрастная группа:</strong> {participantData.age_group_name ?? 'Неизвестно'}</Typography>
        <Typography variant="body1"><strong>Вес:</strong> {participantData.weight ?? 'Неизвестно'}</Typography>
      </Box>

      {participantData.ranks?.length ? (
        <Box mb={3}>
          <Typography variant="body1"><strong>Разряды:</strong></Typography>
          <Box mt={1} display="flex" flexDirection="column" gap={1.5}>
            {groupRanksByType(participantData.ranks).map((g) => (
              <Box key={g.type}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Tooltip title={rankTitle(g.type)} arrow>
                    <Chip
                      label={g.rows.length > 1 ? `${rankDisplayLabel(g.type)}×${g.rows.length}` : rankDisplayLabel(g.type)}
                      color={rankChipColor(g.type)}
                      variant="outlined"
                      size="small"
                      sx={{ fontWeight: 700 }}
                    />
                  </Tooltip>
                  <Typography variant="body2" color="textSecondary">
                    {rankTitle(g.type)}
                  </Typography>
                </Box>

                <Box mt={0.5} ml={0.5}>
                  {g.rows
                    .slice()
                    .sort((a, b) => String(a.contest_name).localeCompare(String(b.contest_name)))
                    .map((r) => {
                      const thr = formatRankThreshold(r);
                      return (
                        <Typography key={`${g.type}:${r.rank_id}:${r.contest_id}:${r.result_group ?? ''}`} variant="body2" sx={{ lineHeight: 1.35 }}>
                          • {r.contest_name}
                          {r.result_group ? ` — ${r.result_group}` : ''}
                          {thr !== null ? ` (порог: ${thr})` : ''}
                        </Typography>
                      );
                    })}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}

      <Box mb={3}>
        <Typography variant="body1"><strong>О себе:</strong></Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
          {participantData.info ?? 'Нет данных'}
        </Typography>
      </Box>

      <Typography variant="h6" gutterBottom color="primary">Ссылки участника</Typography>
      {participantData.links?.length ? (
        participantData.links.map((link, index) => (
          <Box key={index} mb={2}>
            <Link href={link.link} target="_blank" rel="noopener noreferrer" sx={{ color: '#1976d2', textDecoration: 'none' }}>
              {link.link}
            </Link>
            <Typography variant="body2" color="textSecondary">{link.description}</Typography>
          </Box>
        ))
      ) : (
        <Typography variant="body2">Нет доступных ссылок.</Typography>
      )}
    </Paper>
  );
};

export default ParticipantProfilePage;
